import React from 'react';
import ServicesContext from '../contexts/ServicesContext';
import { FileFormat } from '../enums/FileFormat';
import { SeparationType } from '../enums/SeparationType';
import { OrderBatch } from '../models/OrderBatch';
import { OrderForListing } from '../models/OrderForListing';
import { strings } from '../resources/strings';
import { GetDanfePackingLabelInput } from '../services/Labels/GetDanfePackingLabel/GetDanfePackingLabelInput';
import { GetLensCertificateLabelInput } from '../services/Labels/GetLensCertificateLabel/GetLensCertificateLabelInput';
import { GetSimplePackingLabelInput } from '../services/Labels/GetSimplePackingLabel/GetSimplePackingLabelInput';
import { GenerateSeparationListReportInput } from '../services/Reports/GenerateSeparationListReport/GenerateSeparationListReportInput';
import { GenerateShippingGuideReportInput } from '../services/Reports/GenerateShippingGuideReport/GenerateShippingGuideReportInput';
import { GenerateTSOReportInput } from '../services/Reports/GenerateTSOReport/GenerateTSOReportInput';
import { GenerateLensSeparationListReportInput } from '../services/Reports/GenerateLensSeparationListReport/GenerateLensSeparationListReportInput';
import { StatePrescriptionIdentification } from '../enums/StatePrescriptionIdentification';

export function usePrintListOrders() {

    const {
        generateSeparationListReportService,
        generateLensSeparationListReportService,
        generateShippingGuideReportService,
        generateTSOReportService,
        getDanfePackingLabelService,
        getSimplePackingLabelService,
        getLensCertificateLabelService,
        toastService,
        alertService
    } = React.useContext(ServicesContext);

    function getSelectedOrderIds(
        ordersForListing: OrderForListing[]
    ) {
        return ordersForListing?.filter(xo => xo._selected)?.map(xo => xo.order.id) || [];
    }

    function getSelectedOrders(
      ordersForListing: OrderForListing[]
  ) {
      return ordersForListing?.filter(xo => xo._selected) || [];
  }

    function printListToSeparate(
        separationType: SeparationType,
        ordersForListing: OrderForListing[],
        orderBatch?: OrderBatch
    ) {
        const input = new GenerateSeparationListReportInput();
        input.orderIds = getSelectedOrderIds(ordersForListing);
        input.title = '';
        if (separationType === SeparationType.Prescription) {
            input.title = `${strings.ListOfSeparation} - ${strings.OrderWithDegree}`;
        } else if (separationType === SeparationType.NonPrescription) {
            input.title = `${strings.ListOfSeparation} - ${strings.OrderNonDegree}`;
        } else if (separationType === SeparationType.HomeTryOn) {
            input.title = `${strings.ListOfSeparation} - ${strings.ZerezesAtHome}`;
        }
        if (orderBatch?.id) {
            input.title += ` - ${strings.Batch} ${orderBatch.id}`;
        }
        generateSeparationListReportService.handle(input);
    }

    async function printLensSeparationList(fileFormat: FileFormat, ordersForListing: OrderForListing[]) {
      if (fileFormat != FileFormat.Pdf && fileFormat != FileFormat.Csv) {
        toastService.warning(strings.MessageToFileFormatInvalid);
        return;
      }

      const selectedOrders = getSelectedOrders(ordersForListing);
      const numberOfOrders = selectedOrders.length;
      const numberOfOrdersThatAreWaitingLenses = selectedOrders.filter(order => 
              order.statePrescriptionLog?.currentStateId === StatePrescriptionIdentification.AguardandoLente).length;
      const numberOfOrdersThatAreNotWaitingLenses = numberOfOrders - numberOfOrdersThatAreWaitingLenses;
      if (numberOfOrdersThatAreNotWaitingLenses > 0) {
        let message = '';
        if (numberOfOrdersThatAreWaitingLenses === 0) {
          message = `Nenhum pedido selecionado aguardando lente.`;
        } else {
          message = `De ${numberOfOrders} pedidos selecionados, `;
          if (numberOfOrdersThatAreWaitingLenses > 1) {
            message += ` ${numberOfOrdersThatAreWaitingLenses} estão aguardando lente.`;
          } else {
            message += ` um está aguardando lente.`;
          }
        }
        message += `<br>Deseja realmente continuar?`;
        const confirmation = await alertService.confirmHtml(message);
        if (!confirmation.isConfirmed) return;
      }

      const input = new GenerateLensSeparationListReportInput();
      input.orderIds = getSelectedOrderIds(selectedOrders);
      input.title = `Lista de Separação de Lentes`;
      
      if (fileFormat == FileFormat.Pdf) {
        generateLensSeparationListReportService.handlePdf(input);
        return;
      }

      if (fileFormat == FileFormat.Csv) {
        generateLensSeparationListReportService.handleCsv(input);
        return;
      }
    }

    function printShippingGuide(
        ordersForListing: OrderForListing[],
        version?: number
    ) {
        const input = new GenerateShippingGuideReportInput();
        input.orderIds = getSelectedOrderIds(ordersForListing);
        if (!input.orderIds || input.orderIds.length <= 0) {
            toastService.warning(strings.MessageToPrintingSelectedOrdersEmpty);
            return;
        }
        generateShippingGuideReportService.handle(input, version);
    }

    function printTSO(
        ordersForListing: OrderForListing[],
        version?: number
    ) {
        const input = new GenerateTSOReportInput();
        input.orderIds = getSelectedOrderIds(ordersForListing);
        if (!input.orderIds || input.orderIds.length <= 0) {
            toastService.warning(strings.MessageToPrintingSelectedOrdersEmpty);
            return;
        }
        generateTSOReportService.handle(input, version);
    }

    function printZplDanfePackingLabel(ordersForListing: OrderForListing[]) {
        printDanfePackingLabel(ordersForListing, FileFormat.Zpl);
    }

    function printPdfDanfePackingLabel(ordersForListing: OrderForListing[]) {
        printDanfePackingLabel(ordersForListing, FileFormat.Pdf);
    }

    async function printDanfePackingLabel(
        ordersForListing: OrderForListing[],
        fileFormat: FileFormat
    ) {
        const fileFormatIsValid = fileFormat === FileFormat.Zpl || fileFormat === FileFormat.Pdf;

        if (!fileFormatIsValid) {
            toastService.warning(strings.MessageToFileFormatInvalid);
            return;
        }

        const inputWithAllSelectedOrders = new GetDanfePackingLabelInput();
        inputWithAllSelectedOrders.orderIds = getSelectedOrderIds(ordersForListing);
        if (!inputWithAllSelectedOrders.orderIds || inputWithAllSelectedOrders.orderIds.length <= 0) {
            toastService.warning(strings.MessageToPrintingSelectedOrdersEmpty);
            return;
        }
        
        const result = await getDanfePackingLabelService.dataHandle(inputWithAllSelectedOrders);
        const list = result?.value || [];
        
        const invalidDanfeLabels = list.filter(xd => !xd.hasEnoughDataToGenerateDanfe);
        if (invalidDanfeLabels.length > 0) {
            const validDanfeLabels = list.filter(xd => xd.alerts.length === 0 && xd.hasEnoughDataToGenerateDanfe);
            if (validDanfeLabels.length > 0) {
                const firstMessage = validDanfeLabels.length > 1 
                        ? `Apenas ${validDanfeLabels.length} etiquetas de ${list.length} podem ser geradas no formato danfe. Deseja continuar?`
                        : `Apenas uma etiqueta de ${list.length} pode ser gerada no formato danfe. Deseja continuar?`;
                const firstConfirmation = await alertService.confirm(firstMessage);
                if (firstConfirmation.isConfirmed)   {
                    const inputValidLabels = new GetDanfePackingLabelInput();
                    inputValidLabels.orderIds = validDanfeLabels.map(xvl => xvl.orderId);
                    
                    if (fileFormat === FileFormat.Pdf)
                        getDanfePackingLabelService.pdfHandle(inputValidLabels);
                    else
                        getDanfePackingLabelService.fileHandle(inputValidLabels);

                    const secondMessage = invalidDanfeLabels.length > 1
                            ? `Ainda podemos gerar ${invalidDanfeLabels.length} etiquetas de ${list.length} no formato simples. Deseja continuar?`
                            : `Ainda podemos gerar uma etiqueta de ${list.length} no formato simples. Deseja continuar?`;
                    const secondConfirmation = await alertService.confirm(secondMessage);
                    if (secondConfirmation.isConfirmed)   {
                        const inputInvalidLabels = new GetDanfePackingLabelInput();
                        inputInvalidLabels.orderIds = invalidDanfeLabels.map(xvl => xvl.orderId);

                        if (fileFormat === FileFormat.Pdf)
                            getSimplePackingLabelService.pdfHandle(inputInvalidLabels);
                        else
                            getSimplePackingLabelService.fileHandle(inputInvalidLabels);
                    }
                }
            } else {
                const message = `Nenhuma etiqueta pode ser gerada no formato danfe. Deseja gerar as etiquetas no formato simples?`;
                const confirmation = await alertService.confirm(message);
                if (confirmation.isConfirmed)   {
                    if (fileFormat === FileFormat.Pdf)
                        getSimplePackingLabelService.pdfHandle(inputWithAllSelectedOrders);
                    else
                        getSimplePackingLabelService.fileHandle(inputWithAllSelectedOrders);
                }
            }
            return;
        }

        const invalidLabels = list.filter(xd => xd.alerts.length > 0);
        if (invalidLabels.length > 0) {
            const validLabels = list.filter(xd => xd.alerts.length === 0);
            if (validLabels.length > 0) {
                const firstMessage = validLabels.length > 1 
                        ? `Apenas ${validLabels.length} etiquetas de ${list.length} podem ser geradas. Deseja continuar?`
                        : `Apenas uma etiqueta de ${list.length} pode ser gerada. Deseja continuar?`;
                const firstConfirmation = await alertService.confirm(firstMessage);
                if (firstConfirmation.isConfirmed)   {
                    const inputValidLabels = new GetDanfePackingLabelInput();
                    inputValidLabels.orderIds = validLabels.map(xvl => xvl.orderId);

                    if (fileFormat === FileFormat.Pdf)
                        getDanfePackingLabelService.pdfHandle(inputValidLabels);
                    else
                        getDanfePackingLabelService.fileHandle(inputValidLabels);
                }
            } else {
                const message = `Nenhuma etiqueta pode ser gerada!`;
                await alertService.warning(message);
            }
            return;
        }

        if (fileFormat === FileFormat.Pdf)
            getDanfePackingLabelService.pdfHandle(inputWithAllSelectedOrders);
        else
            getDanfePackingLabelService.fileHandle(inputWithAllSelectedOrders);
    }

    function printZplSimplePackingLabel(
        ordersForListing: OrderForListing[]
    ) {
        const input = new GetSimplePackingLabelInput();
        input.orderIds = getSelectedOrderIds(ordersForListing);
        if (!input.orderIds || input.orderIds.length <= 0) {
            toastService.warning(strings.MessageToPrintingSelectedOrdersEmpty);
            return;
        }
        getSimplePackingLabelService.fileHandle(input);
    }

    function printPdfSimplePackingLabel(
        ordersForListing: OrderForListing[]
    ) {
        const input = new GetSimplePackingLabelInput();
        input.orderIds = getSelectedOrderIds(ordersForListing);
        if (!input.orderIds || input.orderIds.length <= 0) {
            toastService.warning(strings.MessageToPrintingSelectedOrdersEmpty);
            return;
        }
        getSimplePackingLabelService.pdfHandle(input);
    }

    function printZplLensCertificateLabel(
        ordersForListing: OrderForListing[]
    ) {
        const input = new GetLensCertificateLabelInput();
        input.orderIds = getSelectedOrderIds(ordersForListing);
        if (!input.orderIds || input.orderIds.length <= 0) {
            toastService.warning(strings.MessageToPrintingSelectedOrdersEmpty);
            return;
        }
        getLensCertificateLabelService.fileHandle(input);
    }

    function printPdfLensCertificateLabel(
        ordersForListing: OrderForListing[]
    ) {
        const input = new GetLensCertificateLabelInput();
        input.orderIds = getSelectedOrderIds(ordersForListing);
        if (!input.orderIds || input.orderIds.length <= 0) {
            toastService.warning(strings.MessageToPrintingSelectedOrdersEmpty);
            return;
        }
        getLensCertificateLabelService.pdfHandle(input);
    }

    return {
        printListToSeparate,
        printLensSeparationList,
        printShippingGuide,
        printTSO,
        printZplDanfePackingLabel,
        printPdfDanfePackingLabel,
        printZplSimplePackingLabel,
        printPdfSimplePackingLabel,
        printZplLensCertificateLabel,
        printPdfLensCertificateLabel
    };
}